<template>
  <div v-if="siteDomain">
    <div class="flex flex-col justify-center sm:flex-row gap-3 sm:gap-4">
      <!-- Google -->
      <UIButton
        class="flex-1"
        button="external"
        type="button"
        @click="redirectToAppSignin('google')"
      >
        <img :src="googleIcon" alt="Google icon" class="w-5 h-5" />
        Google
      </UIButton>
      <!-- Microsoft -->
      <UIButton
        class="flex-1"
        button="external"
        type="button"
        @click="redirectToAppSignin('microsoft')"
      >
        <img :src="microsoftIcon" alt="Microsoft icon" class="w-5 h-5" />
        Microsoft
      </UIButton>
      <!-- Apple -->
      <UIButton class="flex-1 hidden" button="external" @click="onAppleClick">
        <img :src="appleIcon" alt="Apple icon" class="w-5 h-5" />
        Apple
      </UIButton>
    </div>
    <div class="font-medium text-primary-gray-900 text-center mt-4">OR</div>
  </div>
</template>

<script>
import { siteDomainHandler, siteConstants, isLocalHost } from '@src/utils/site-utils.js'
import UIButton from '@components/UiElements/UIButton.vue'
import {
  redirectToGoogleLogin as redirectToGoogleLoginUtil,
  handleGoogleOAuthCallback as handleGoogleOAuthCallbackUtil,
  hasHandledGoogleCallback as hasHandledGoogleCallbackUtil,
  setGoogleOAuthHandled,
  clearGoogleOAuthHandled,
} from '@src/lib/third-party-signin/google.js'
import {
  redirectToMicrosoftLogin as redirectToMicrosoftLoginUtil,
  handleMicrosoftOAuthCallback as handleMicrosoftOAuthCallbackUtil,
  isMicrosoftCallback,
  hasHandledMicrosoftCallback as hasHandledMicrosoftCallbackUtil,
  setMicrosoftOAuthHandled,
  clearMicrosoftOAuthHandled,
} from '@src/lib/third-party-signin/microsoft.js'

const GOOGLE_ICON_URL = 'https://www.svgrepo.com/show/475656/google-color.svg'
const MICROSOFT_ICON_URL = 'https://www.svgrepo.com/show/452062/microsoft.svg'
const APPLE_ICON_URL = 'https://www.svgrepo.com/show/511330/apple-173.svg'
export default {
  name: 'ThirdPartySignin',
  components: {
    UIButton,
  },
  props: {
    rememberMe: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['google-login', 'microsoft-login'],
  data() {
    return {
      loginProvider: 'google',
      querySubDomain: '',
      googleIcon: GOOGLE_ICON_URL,
      microsoftIcon: MICROSOFT_ICON_URL,
      appleIcon: APPLE_ICON_URL,
      currentHostName: window.location.hostname,
      hasHandledGoogleCallback: false,
      hasHandledMicrosoftCallback: false,
    }
  },
  computed: {
    siteDomain() {
      return siteDomainHandler().domain
    },
  },
  beforeCreate() {
    if (hasHandledGoogleCallbackUtil()) {
      this.hasHandledGoogleCallback = true
    }
    if (hasHandledMicrosoftCallbackUtil()) {
      this.hasHandledMicrosoftCallback = true
    }
  },
  created() {
    const hash = window.location.hash.substring(1)
    const searchParams = new URLSearchParams(window.location.search)
    const hasGoogleCallback = hash && (hash.includes('access_token') || hash.includes('error'))
    const hasMicrosoftCallback =
      searchParams.has('code') || (searchParams.has('error') && searchParams.has('state'))

    if (hasMicrosoftCallback && isMicrosoftCallback(searchParams)) {
      this.handleMicrosoftOAuthCallback()
    } else if (hasGoogleCallback) {
      this.handleGoogleOAuthCallback()
    } else {
      this.querySubDomain = this.$route.query.sub_domain
      this.loginProvider = this.$route?.query?.provider || 'google'
      clearGoogleOAuthHandled()
      clearMicrosoftOAuthHandled()
    }
  },
  mounted() {
    const shouldStartGoogle = this.shouldAutoStartGoogleLogin()
    const shouldStartMicrosoft = this.shouldAutoStartMicrosoftLogin()
    if (shouldStartGoogle && !this.hasHandledGoogleCallback) this.redirectToGoogleLogin()
    else if (shouldStartMicrosoft && !this.hasHandledMicrosoftCallback)
      this.redirectToMicrosoftLogin()
  },
  methods: {
    shouldAutoStartLogin(provider) {
      const hasSubDomain = Boolean(this.querySubDomain)
      const isProvider = this.loginProvider === provider
      const isLocal = isLocalHost()
      const isProdDomain = siteConstants.MYQAMPUS_PROD_MAIN_DOMAIN
      const isBetaDomain = siteConstants.MYQAMPUS_BETA_DOMAIN
      const isAppDomain = isProdDomain || isBetaDomain || isLocal
      return isProvider && isAppDomain && hasSubDomain
    },
    shouldAutoStartGoogleLogin() {
      return this.shouldAutoStartLogin('google')
    },
    shouldAutoStartMicrosoftLogin() {
      return this.shouldAutoStartLogin('microsoft')
    },
    redirectToAppSignin(provider = null) {
      clearGoogleOAuthHandled()
      clearMicrosoftOAuthHandled()
      const [subdomain, ...rest] = this.currentHostName.split('.')
      const appDomain = `app.${rest.join('.')}`
      const params = new URLSearchParams({ sub_domain: subdomain })
      if (provider) {
        params.set('provider', provider)
      }
      const redirectUrl = isLocalHost()
        ? `http://${window.location.host}/signin?${params.toString()}`
        : `https://${appDomain}/signin?${params.toString()}`
      window.location.href = redirectUrl
    },
    redirectToGoogleLogin() {
      const redirectUri = `${window.location.origin}${window.location.pathname}`
      redirectToGoogleLoginUtil(redirectUri, this.querySubDomain, this.loginProvider)
    },
    handleGoogleOAuthCallback() {
      const hash = window.location.hash.substring(1)
      const result = handleGoogleOAuthCallbackUtil(hash)
      this.hasHandledGoogleCallback = true
      setGoogleOAuthHandled()
      window.history.replaceState(null, '', window.location.pathname)

      if (result?.error) {
        this.$store.commit(
          'toast/NEW',
          { type: 'error', message: result.errorMessage },
          { root: true },
        )
        return
      }

      if (result) {
        this.handleGoogleLogin({
          access_token: result.access_token,
          provider: result.provider,
          sub_domain: result.sub_domain,
        })
      }
    },
    handleGoogleLogin(response) {
      if (response?.error) {
        this.$store.commit(
          'toast/NEW',
          { type: 'error', message: 'Error in token response' },
          { root: true },
        )
        clearGoogleOAuthHandled()
        this.hasHandledGoogleCallback = false
        return
      }
      const payload = {
        sub_domain: response.sub_domain,
        access_token: response.access_token,
        provider: response.provider,
        remember_me: this.rememberMe || false,
      }
      this.$emit('google-login', payload)
    },
    async redirectToMicrosoftLogin() {
      const redirectUri = `${window.location.origin}${window.location.pathname}`
      await redirectToMicrosoftLoginUtil(redirectUri, this.querySubDomain, this.loginProvider)
    },
    async handleMicrosoftOAuthCallback() {
      const searchParams = new URLSearchParams(window.location.search)
      this.hasHandledMicrosoftCallback = true
      setMicrosoftOAuthHandled()

      const result = await handleMicrosoftOAuthCallbackUtil(searchParams)

      window.history.replaceState(null, '', window.location.pathname)

      if (result?.error) {
        this.$store.commit(
          'toast/NEW',
          { type: 'error', message: result.errorMessage },
          { root: true },
        )
        return
      }

      if (result) {
        this.handleMicrosoftLogin({
          access_token: result.access_token,
          id_token: result.id_token,
          provider: result.provider,
          sub_domain: result.sub_domain,
        })
      }
    },
    handleMicrosoftLogin(response) {
      if (response?.error) {
        this.$store.commit(
          'toast/NEW',
          { type: 'error', message: 'Error in token response' },
          { root: true },
        )
        clearMicrosoftOAuthHandled()
        this.hasHandledMicrosoftCallback = false
        return
      }
      const payload = {
        sub_domain: response.sub_domain,
        access_token: response.access_token,
        id_token: response.id_token,
        provider: response.provider,
        remember_me: this.rememberMe || false,
      }
      this.$emit('microsoft-login', payload)
    },
    onAppleClick() {
      console.log('Apple signup clicked')
    },
  },
}
</script>
